import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Heart, Cake, Crown, Users, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const formatDisplayId = (id) => {
  if (!id) return '';
  return id.replace(/([a-zA-Z]+)(\d+)/, (_, word, num) =>
    word.charAt(0).toUpperCase() + word.slice(1) + ' ' + num
  );
};

const ICONS = { wedding: Heart, birthday: Cake, golden: Crown, public_event: Users };
const COLORS = ["#D4A76A", "#E06B8A", "#C9A84C", "#198754", "#5B8DEF", "#9B59B6", "#E67E22"];

function PackageEnquiryForm({ pkg, onClose }) {
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const equipList = pkg.equipment_items
      ? pkg.equipment_items.map(i => `${i.equipment} x${i.quantity}`).join(', ')
      : 'N/A';

    const message = [
      `Package Enquiry`,
      ``,
      `Package: ${pkg.name}`,
      pkg.day_rate != null ? `Day Rate: £${pkg.day_rate}` : '',
      `Equipment: ${equipList}`,
      `Phone: ${phone}`,
      comment ? `Comment: ${comment}` : '',
    ].filter(Boolean).join('\n');

    try {
      await axios.post(`${API}/contact`, { name: `Package Enquiry - ${pkg.name}`, email, message });
      toast.success("Enquiry sent! We'll get back to you shortly.");
      onClose();
    } catch {
      toast.error('Failed to send enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="enquiry-overlay" onClick={onClose} data-testid="enquiry-overlay">
      <div className="enquiry-modal" onClick={(e) => e.stopPropagation()} data-testid="enquiry-modal">
        <button className="enquiry-close" onClick={onClose} aria-label="Close" data-testid="enquiry-close-btn">
          <X size={20} />
        </button>
        <h2 className="enquiry-title">Package Enquiry</h2>
        <div className="enquiry-pkg-name" data-testid="enquiry-pkg-name">{pkg.name}</div>
        {pkg.day_rate != null && (
          <p className="enquiry-pkg-rate">&pound;{pkg.day_rate} / day</p>
        )}
        <form onSubmit={handleSubmit} className="enquiry-form">
          <div className="form-group">
            <Label className="form-label">Phone Number</Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              placeholder="Your phone number"
              data-testid="enquiry-phone-input"
            />
          </div>
          <div className="form-group">
            <Label className="form-label">Email Address</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Your email address"
              data-testid="enquiry-email-input"
            />
          </div>
          <div className="form-group">
            <Label className="form-label">Comment <span style={{ fontWeight: 400, color: 'var(--text-light)', fontSize: '0.85rem' }}>(optional)</span></Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Any questions or special requirements..."
              data-testid="enquiry-comment-input"
            />
          </div>
          <Button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={isSubmitting} data-testid="enquiry-submit-btn">
            {isSubmitting ? 'Sending...' : 'Send Enquiry'}
          </Button>
        </form>
      </div>
    </div>
  );
}

function EquipmentItemRow({ item, galleryLookup }) {
  const [showImage, setShowImage] = useState(false);
  const info = galleryLookup[item.equipment];
  const name = info?.caption || formatDisplayId(item.equipment);
  const imageUrl = info ? getImageUrl(info.image_url) : null;

  return (
    <li className="package-item" data-testid={`pkg-equip-item-${item.equipment}`}>
      <div
        className={`package-item-row ${imageUrl ? 'clickable' : ''}`}
        onClick={() => imageUrl && setShowImage(!showImage)}
      >
        <span className="package-item-name">{name}</span>
        <span className="package-item-qty">&times;{item.quantity}</span>
      </div>
      {showImage && imageUrl && (
        <div className="package-item-image" data-testid={`pkg-equip-img-${item.equipment}`}>
          <img src={imageUrl} alt={name} loading="lazy" />
        </div>
      )}
    </li>
  );
}

function PackageCard({ pkg, index, galleryLookup, onEnquire }) {
  const color = COLORS[index % COLORS.length];
  const iconKey = pkg.name ? pkg.name.toLowerCase().replace(/\s+/g, '_') : 'default';
  const Icon = ICONS[iconKey] || Crown;

  return (
    <div className="package-card" data-testid={`package-card-${pkg.id}`}>
      <div className="package-header" style={{ borderBottomColor: color }}>
        <div className="package-icon">
          <Icon size={30} color={color} />
        </div>
        <h3 className="package-name">{pkg.name}</h3>
        {pkg.day_rate != null && (
          <p className="package-price" style={{ color }}>
            &pound;{pkg.day_rate}
            <span className="package-price-label"> / day</span>
          </p>
        )}
      </div>
      <div className="package-body">
        {pkg.description && <p className="package-desc">{pkg.description}</p>}
        {pkg.equipment_items && pkg.equipment_items.length > 0 && (
          <ul className="package-items">
            {pkg.equipment_items.map((item, j) => (
              <EquipmentItemRow key={j} item={item} galleryLookup={galleryLookup} />
            ))}
          </ul>
        )}
        <div className="package-cta">
          <Button className="btn-primary package-btn" onClick={() => onEnquire(pkg)} data-testid={`package-enquire-${pkg.id}`}>
            Enquire Now
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PackagesPage({ Navigation, Footer }) {
  const [packages, setPackages] = useState([]);
  const [galleryLookup, setGalleryLookup] = useState({});
  const [enquiryPkg, setEnquiryPkg] = useState(null);

  useEffect(() => {
    document.title = 'Equipment Hire Packages | Eloquent Voices London';

    Promise.all([
      axios.get(`${API}/packages`),
      axios.get(`${API}/galleries`)
    ]).then(([pkgRes, galRes]) => {
      setPackages(pkgRes.data);
      const lookup = {};
      Object.values(galRes.data).forEach(images => {
        images.forEach(img => {
          if (img.display_id) {
            lookup[img.display_id] = { caption: img.caption, image_url: img.image_url };
          }
        });
      });
      setGalleryLookup(lookup);
    }).catch(() => {});

    const faqSchema = document.createElement('script');
    faqSchema.type = 'application/ld+json';
    faqSchema.id = 'packages-faq-schema';
    faqSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {"@type": "Question", "name": "Can I mix items from different packages?", "acceptedAnswer": {"@type": "Answer", "text": "Absolutely. Our packages are starting points. We can add or remove any item to match your exact requirements."}},
        {"@type": "Question", "name": "Do you deliver and set up the equipment?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. We offer delivery, setup assistance, and collection across London. Setup guidance is included with every hire."}},
        {"@type": "Question", "name": "How far in advance should I book?", "acceptedAnswer": {"@type": "Answer", "text": "We recommend booking at least two weeks before your event, especially during peak season (May to September). However, last-minute bookings are welcome subject to availability."}},
        {"@type": "Question", "name": "What happens if equipment is damaged?", "acceptedAnswer": {"@type": "Answer", "text": "Normal wear and tear is covered. For any accidental damage, we offer affordable damage waiver options at checkout."}}
      ]
    });
    document.head.appendChild(faqSchema);

    return () => {
      document.title = 'Eloquent Voices | Audio Equipment Hire London';
      const el = document.getElementById('packages-faq-schema');
      if (el) el.remove();
    };
  }, []);

  return (
    <>
      <Navigation />
      <main className="packages-main">
        <section className="packages-hero">
          <div className="container">
            <h1 data-testid="packages-heading">Our Packages</h1>
            <p>Tailored equipment bundles for every occasion. Choose a ready-made package or request a custom quote.</p>
          </div>
        </section>
        <section className="section">
          <div className="container">
            <div style={{ maxWidth: '800px', margin: '0 auto 40px', color: 'var(--text-muted)', lineHeight: '1.7', fontSize: '0.95rem' }} data-testid="packages-intro-text">
              <p style={{ marginBottom: '16px' }}>At Eloquent Voices we offer curated equipment packages designed for the most common event types in London. Whether you are planning a wedding reception, a milestone birthday, a corporate function, or a large public gathering, our packages include everything you need for professional-quality sound, lighting, and staging.</p>
              <p style={{ marginBottom: '16px' }}>Each package comes with delivery, setup guidance, and collection. All equipment is PAT-tested and maintained to the highest standards. Need something specific? We can customise any package to suit your venue, guest count, and budget.</p>
              <p>Browse the options below and click <strong>Enquire Now</strong> to request a personalised quote. We typically respond within 24 hours.</p>
            </div>
            {packages.length === 0 ? (
              <div className="packages-empty">
                <Crown size={48} color="var(--text-light)" />
                <p>Packages coming soon. <Link to="/#contact" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Contact us</Link> for a custom quote in the meantime.</p>
              </div>
            ) : (
              <div className="packages-grid">
                {packages.map((pkg, i) => (
                  <PackageCard key={pkg.id} pkg={pkg} index={i} galleryLookup={galleryLookup} onEnquire={setEnquiryPkg} />
                ))}
              </div>
            )}
            <div style={{ maxWidth: '800px', margin: '48px auto 0', color: 'var(--text-muted)', lineHeight: '1.7', fontSize: '0.95rem' }} data-testid="packages-faq-text">
              <h2 style={{ fontSize: '1.3rem', color: 'var(--text-main)', marginBottom: '20px' }}>Frequently Asked Questions</h2>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontWeight: 600, color: 'var(--text-main)', marginBottom: '4px' }}>Can I mix items from different packages?</p>
                <p>Absolutely. Our packages are starting points. We can add or remove any item to match your exact requirements.</p>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontWeight: 600, color: 'var(--text-main)', marginBottom: '4px' }}>Do you deliver and set up the equipment?</p>
                <p>Yes. We offer delivery, setup assistance, and collection across London. Setup guidance is included with every hire.</p>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontWeight: 600, color: 'var(--text-main)', marginBottom: '4px' }}>How far in advance should I book?</p>
                <p>We recommend booking at least two weeks before your event, especially during peak season (May to September). However, last-minute bookings are welcome subject to availability.</p>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontWeight: 600, color: 'var(--text-main)', marginBottom: '4px' }}>What happens if equipment is damaged?</p>
                <p>Normal wear and tear is covered. For any accidental damage, we offer affordable damage waiver options at checkout.</p>
              </div>
              <div style={{ textAlign: 'center', marginTop: '32px' }}>
                <Link to="/#contact"><Button className="btn-primary" data-testid="packages-cta-btn">Get a Custom Quote</Button></Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      {enquiryPkg && <PackageEnquiryForm pkg={enquiryPkg} onClose={() => setEnquiryPkg(null)} />}
    </>
  );
}
